import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useAccessStatus, useChatConfigured } from "../access/api";
import { roleLabel } from "../admin-users/roles";
import { CELL_KEY, DURATION_KEY, SEE_KEY, durationParts, festlegungen, parameters, type StatusValue } from "./status";

/**
 * The status of the installation ([F-025], [AC-045], [AC-069]): the running
 * application names its Festlegungen with their effective values, the roles,
 * the rights matrix and every operating parameter of [NF-019] with value and
 * origin. An installation that does not say how it is set up can disagree
 * with the specification without anybody noticing. Administration only - the
 * server refuses everybody else, and the page says so.
 *
 * Also here: whether the chat delivery of reminders is configured ([F-016]),
 * when the API reports it.
 */
export function AccessStatusPage(): JSX.Element {
  const { t } = useTranslation();
  const status = useAccessStatus();
  const chat = useChatConfigured(status.isSuccess);

  if (status.isPending) {
    return (
      <p data-testid="admin-status_loading" className="text-sm text-muted-foreground">
        {t("common.loading")}
      </p>
    );
  }
  if (status.isError) {
    return (
      <p data-testid="admin-status_forbidden" role="alert" className="text-sm text-muted-foreground">
        {t("adminUsers.forbidden")}
      </p>
    );
  }

  const data = status.data;
  const roles = data.authorization.roles;

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h1 data-testid="admin-status_heading" className="text-2xl font-semibold tracking-tight">
          {t("adminStatus.title")}
        </h1>
        <p className="max-w-3xl text-sm text-muted-foreground">{t("adminStatus.lead")}</p>
        <p data-testid="admin-status_module" className="text-xs text-muted-foreground">
          {t("adminStatus.module", { module: data.module, version: data.version })}
        </p>
      </div>

      <Block testid="admin-status_festlegungen" title={t("adminStatus.festlegungenTitle")}>
        <Table data-testid="admin-status_festlegungen-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t("adminStatus.colKey")}</TableHead>
              <TableHead>{t("adminStatus.colQuestion")}</TableHead>
              <TableHead>{t("adminStatus.colValue")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {festlegungen(data).map((row) => (
              <TableRow key={row.key} data-testid={`admin-status_festlegung_${row.key}`}>
                <TableCell className="whitespace-nowrap font-mono text-xs">{row.key}</TableCell>
                <TableCell className="text-sm">{t(row.questionKey)}</TableCell>
                <TableCell className="text-sm">
                  <Value value={row.value} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Block>

      <Block testid="admin-status_roles" title={t("adminStatus.rolesTitle")}>
        <Table data-testid="admin-status_roles-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t("adminStatus.colRoleId")}</TableHead>
              <TableHead>{t("adminStatus.colRoleLabel")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id} data-testid={`admin-status_role_${role.id}`}>
                <TableCell className="font-mono text-xs">{role.id}</TableCell>
                <TableCell className="text-sm">
                  {role.label}
                  {data.authorization.adminRole === role.id ? (
                    <Badge variant="outline" className="ml-2">
                      {t("adminStatus.administration")}
                    </Badge>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Block>

      <Block testid="admin-status_relations" title={t("adminStatus.relationsTitle")}>
        <Table data-testid="admin-status_relations-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t("adminStatus.colRelation")}</TableHead>
              <TableHead>{t("adminStatus.colMeaning")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.authorization.relations.map((relation) => (
              <TableRow key={relation.id} data-testid={`admin-status_relation_${relation.id}`}>
                <TableCell className="font-mono text-xs">{relation.id}</TableCell>
                <TableCell className="text-sm">{relation.meaning}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Block>

      <Block testid="admin-status_matrix" title={t("adminStatus.matrixTitle")} lead={t("adminStatus.matrixLead")}>
        <Table data-testid="admin-status_matrix-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t("adminStatus.colAction")}</TableHead>
              <TableHead>{t("adminStatus.colRelation")}</TableHead>
              {roles.map((role) => (
                <TableHead key={role.id} className="whitespace-nowrap text-center">
                  {roleLabel(t, role.id, role.label)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.authorization.matrix.map((row) => (
              <TableRow key={row.id} data-testid={`admin-status_matrix-row_${row.id}`}>
                <TableCell>
                  <div className="font-mono text-xs">{row.id}</div>
                  <div className="text-xs text-muted-foreground">{row.label}</div>
                </TableCell>
                <TableCell className="font-mono text-xs">{row.relation ?? t("common.none")}</TableCell>
                {roles.map((role) => {
                  const cell = row.cells[role.id] ?? "no";
                  return (
                    <TableCell key={role.id} className="text-center text-sm">
                      {t(CELL_KEY[cell])}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Block>

      <Block testid="admin-status_parameters" title={t("adminStatus.parametersTitle")} lead={t("adminStatus.parametersLead")}>
        <Table data-testid="admin-status_parameters-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t("adminStatus.colParameter")}</TableHead>
              <TableHead>{t("adminStatus.colValue")}</TableHead>
              <TableHead>{t("adminStatus.colOrigin")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {parameters(data).map((row) => (
              <TableRow key={row.name} data-testid={`admin-status_parameter_${row.name}`}>
                <TableCell className="font-mono text-xs">{row.name}</TableCell>
                <TableCell className="text-sm">
                  <Value value={row.value} />
                </TableCell>
                <TableCell className="text-sm">
                  {row.origin === "environment" ? t("adminStatus.originEnvironment") : t("adminStatus.originDefault")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Block>

      {chat.isSuccess ? (
        <Block testid="admin-status_chat" title={t("adminStatus.chatTitle")}>
          <p data-testid="admin-status_chat-state" className="text-sm">
            {chat.data.configured ? t("adminStatus.chatConfigured") : t("adminStatus.chatNotConfigured")}
          </p>
        </Block>
      ) : null}
    </div>
  );
}

function Block({ testid, title, lead, children }: { testid: string; title: string; lead?: string; children: ReactNode }): JSX.Element {
  return (
    <section data-testid={testid} className="space-y-3">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{title}</h2>
        {lead ? <p className="max-w-3xl text-sm text-muted-foreground">{lead}</p> : null}
      </div>
      <div className="overflow-x-auto rounded-lg border">{children}</div>
    </section>
  );
}

function Value({ value }: { value: StatusValue }): JSX.Element {
  const { t } = useTranslation();
  switch (value.kind) {
    case "code":
      return <span className="font-mono text-xs">{value.value}</span>;
    case "text":
      return <span>{value.value}</span>;
    case "none":
      return <span className="text-muted-foreground">{t("adminStatus.none")}</span>;
    case "see":
      return <span className="text-muted-foreground">{t(SEE_KEY[value.section])}</span>;
    case "duration": {
      const parts = durationParts(value.seconds);
      return (
        <span>
          {t(DURATION_KEY[parts.unit], { n: parts.n })}{" "}
          <span className="font-mono text-xs text-muted-foreground">({value.seconds} s)</span>
        </span>
      );
    }
  }
}
